import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  AutomationWorkspaceResult,
  useAutomationWorkspace,
} from '../hooks/useAutomationWorkspace';
import type { AutomationIndexDirtyDrafts } from '../utils/automationRuntime';
import { useTabState } from './TabStateContext';

const AutomationWorkspaceContext = createContext<AutomationWorkspaceResult | null>(null);
const isIndexPath = (pathname: string): boolean => (
  pathname.replace(/\/+$/, '') === '/index'
);

interface AutomationWorkspaceProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

/**
 * Owns automation drafts and mutation reconciliation above route lifetime.
 * AuthScopedTabStateProvider keys this provider by user, so neither drafts nor
 * idempotency attempts can cross an account boundary.
 */
export const AutomationWorkspaceProvider: React.FC<AutomationWorkspaceProviderProps> = ({
  children,
  enabled = true,
}) => {
  const location = useLocation();
  const isAutomationRoute = isIndexPath(location.pathname);
  const [hasActivated, setHasActivated] = useState(enabled && isAutomationRoute);
  const shouldActivate = enabled && (hasActivated || isAutomationRoute);

  useEffect(() => {
    if (enabled && isAutomationRoute) setHasActivated(true);
  }, [enabled, isAutomationRoute]);

  const {
    indexTabState,
    automationsTabState,
    setAutomationsTabState,
  } = useTabState();
  const requestedView = isIndexPath(location.pathname)
    ? new URLSearchParams(location.search).get('automation')
    : null;
  const loadImportReferences = requestedView === 'import';

  const handleDirtyDraftsChange = useCallback((
    indexId: string,
    drafts: AutomationIndexDirtyDrafts | null,
  ) => {
    setAutomationsTabState((current) => {
      const existing = current.dirtyDraftsByIndex[indexId] || null;
      if (JSON.stringify(existing) === JSON.stringify(drafts)) return current;
      const dirtyDraftsByIndex = { ...current.dirtyDraftsByIndex };
      if (drafts) {
        dirtyDraftsByIndex[indexId] = drafts;
      } else {
        delete dirtyDraftsByIndex[indexId];
      }
      return {
        ...current,
        dirtyDraftsByIndex,
        hasUnsavedChanges: Object.keys(dirtyDraftsByIndex).length > 0,
      };
    });
  }, [setAutomationsTabState]);

  const workspace = useAutomationWorkspace(indexTabState.selectedIndexId, {
    enabled: shouldActivate,
    isActive: isAutomationRoute,
    initialDirtyDraftsByIndex: automationsTabState.dirtyDraftsByIndex,
    loadImportReferences,
    loadConnectorReferences: requestedView === 'import' || requestedView === 'export',
    onDirtyDraftsChange: handleDirtyDraftsChange,
  });

  useEffect(() => {
    if (!automationsTabState.hasUnsavedChanges) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [automationsTabState.hasUnsavedChanges]);

  // Keep the route tree stable; lazily activate the hook, not its provider.
  return (
    <AutomationWorkspaceContext.Provider value={workspace}>
      {children}
    </AutomationWorkspaceContext.Provider>
  );
};

export const useAutomationWorkspaceContext = (): AutomationWorkspaceResult => {
  const workspace = useContext(AutomationWorkspaceContext);
  if (!workspace) {
    throw new Error(
      'useAutomationWorkspaceContext must be used within an AutomationWorkspaceProvider',
    );
  }
  return workspace;
};
